/**
 * Enrichment Service - Arricchimento plot con dati Open Data
 * Versioning system per gestire evoluzione criteri di geoprocessing
 */

const GEOPROCESSING_URL = 'https://webgis.aedit.it/api/v1/mc/geoprocessing/standalone'
const API_KEY = '_tmp_key_for_test_'

// Definizione criteri di geoprocessing
const CRITERIA_DEFINITIONS = [
  {
    id_layer: 44,
    id_criteria_type: 1,
    name: 'Pedologia',
    guid: '964cbf8d-97e9-4073-9ebd-88075f8481fd',
    source: 'DB Pedologico RT 1:10.000',
    sourceUrl: 'https://dati.toscana.it/dataset/dbped',
    description: 'Tessitura del suolo (sabbia, limo, argilla) e sostanza organica'
  },
  {
    id_layer: 43,
    id_criteria_type: 3,
    name: 'Temperatura',
    guid: 'b1c1e8ed-76fa-4f53-850d-8248dc3c1c39',
    source: 'WorldClim',
    sourceUrl: 'https://www.worldclim.org',
    description: 'Temperatura media annuale'
  },
  {
    id_layer: 42,
    id_criteria_type: 3,
    name: 'Quota',
    guid: '57ae051e-3e6f-4ca2-8816-0c297aa3be2e',
    source: 'DTM Toscana',
    sourceUrl: 'https://dati.toscana.it/dataset/dem10mt',
    description: 'Quota media, minima, massima e variazione altimetrica'
  }
]

// Version history
const ENRICHMENT_VERSIONS = {
  "1.0.0": {
    released: "2026-02-01",
    criteria: [
      "964cbf8d-97e9-4073-9ebd-88075f8481fd", // Pedologia
      "b1c1e8ed-76fa-4f53-850d-8248dc3c1c39", // Temperatura
      "57ae051e-3e6f-4ca2-8816-0c297aa3be2e"  // Quota
    ],
    description: "Versione iniziale: pedologia, temperatura, quota"
  }
}

const CURRENT_VERSION = "1.0.0"

/**
 * Definizione indicatori visualizzabili
 * Questa è la fonte unica di verità per le UI
 */
export const ENRICHMENT_INDICATORS = [
  // Pedologia
  {
    id: 'sand',
    category: 'pedology',
    label: 'Sabbia',
    shortLabel: 'Sabbia',
    unit: '%',
    path: 'pedology.sand',
    getValue: (data) => data.pedology?.sand,
    format: (value) => value != null ? `${value}%` : '-',
    showInTable: true,
    showInDetail: true,
    icon: 'fa-tint',
    order: 1
  },
  {
    id: 'silt',
    category: 'pedology',
    label: 'Limo',
    shortLabel: 'Limo',
    unit: '%',
    path: 'pedology.silt',
    getValue: (data) => data.pedology?.silt,
    format: (value) => value != null ? `${value}%` : '-',
    showInTable: false,
    showInDetail: true,
    icon: 'fa-tint',
    order: 2
  },
  {
    id: 'clay',
    category: 'pedology',
    label: 'Argilla',
    shortLabel: 'Argilla',
    unit: '%',
    path: 'pedology.clay',
    getValue: (data) => data.pedology?.clay,
    format: (value) => value != null ? `${value}%` : '-',
    showInTable: true,
    showInDetail: true,
    icon: 'fa-tint',
    order: 3
  },
  {
    id: 'organicMatter',
    category: 'pedology',
    label: 'Sostanza Organica',
    shortLabel: 'Sost. Org.',
    unit: '%',
    path: 'pedology.organicMatter',
    getValue: (data) => data.pedology?.organicMatter,
    format: (value) => value != null ? `${value.toFixed(1)}%` : '-',
    showInTable: true,
    showInDetail: true,
    icon: 'fa-leaf',
    order: 4
  },
  {
    id: 'landUse',
    category: 'pedology',
    label: 'Uso del suolo',
    shortLabel: 'Uso suolo',
    unit: '',
    path: 'pedology.landUse',
    getValue: (data) => data.pedology?.landUse,
    format: (value) => value || '-',
    showInTable: false,
    showInDetail: true,
    icon: 'fa-leaf',
    order: 5
  },
  
  // Quota
  {
    id: 'meanElevation',
    category: 'elevation',
    label: 'Quota media',
    shortLabel: 'Quota',
    unit: 'm',
    path: 'elevation.meanElevation',
    getValue: (data) => data.elevation?.meanElevation,
    format: (value) => value != null ? `${value.toFixed(0)} m` : '-',
    showInTable: true,
    showInDetail: true,
    icon: 'fa-mountain',
    order: 6
  },
  {
    id: 'elevationRange',
    category: 'elevation',
    label: 'Dislivello',
    shortLabel: 'Dislivello',
    unit: 'm',
    path: 'elevation.elevationRange',
    getValue: (data) => data.elevation?.elevationRange,
    format: (value) => value != null ? `${value.toFixed(0)} m` : '-',
    showInTable: true,
    showInDetail: true,
    icon: 'fa-mountain',
    order: 7
  },
  {
    id: 'minElevation',
    category: 'elevation',
    label: 'Quota minima',
    shortLabel: 'Quota min',
    unit: 'm',
    path: 'elevation.minElevation',
    getValue: (data) => data.elevation?.minElevation,
    format: (value) => value != null ? `${value.toFixed(0)} m` : '-',
    showInTable: false,
    showInDetail: true,
    icon: 'fa-mountain',
    order: 8
  },
  {
    id: 'maxElevation',
    category: 'elevation',
    label: 'Quota massima',
    shortLabel: 'Quota max',
    unit: 'm',
    path: 'elevation.maxElevation',
    getValue: (data) => data.elevation?.maxElevation,
    format: (value) => value != null ? `${value.toFixed(0)} m` : '-',
    showInTable: false,
    showInDetail: true,
    icon: 'fa-mountain',
    order: 9
  },
  
  // Temperatura
  {
    id: 'meanTemp',
    category: 'temperature',
    label: 'Temperatura media annuale',
    shortLabel: 'Temp.',
    unit: '°C',
    path: 'temperature.meanTemp',
    getValue: (data) => data.temperature?.meanTemp,
    format: (value) => value != null ? `${value.toFixed(1)} °C` : '-',
    showInTable: true,
    showInDetail: true,
    icon: 'fa-thermometer-half',
    order: 10
  }
]

/**
 * Ottiene gli indicatori per la tabella
 */
export function getTableIndicators() {
  return ENRICHMENT_INDICATORS.filter(ind => ind.showInTable).sort((a, b) => a.order - b.order)
}

/**
 * Ottiene gli indicatori per il dettaglio, raggruppati per categoria
 */
export function getDetailIndicatorsByCategory() {
  const byCategory = {}
  
  ENRICHMENT_INDICATORS
    .filter(ind => ind.showInDetail)
    .sort((a, b) => a.order - b.order)
    .forEach(ind => {
      if (!byCategory[ind.category]) {
        byCategory[ind.category] = []
      }
      byCategory[ind.category].push(ind)
    })
  
  return byCategory
}

/**
 * Ottiene la versione corrente del sistema di enrichment
 */
export function getCurrentVersion() {
  return CURRENT_VERSION
}

/**
 * Verifica se i dati di un plot sono obsoleti
 */
export function isOutdated(plotOpenData) {
  if (!plotOpenData?.version) return true
  return plotOpenData.version !== CURRENT_VERSION
}

/**
 * Calcola la differenza tra versioni
 */
export function getVersionDifference(oldVersion) {
  const current = ENRICHMENT_VERSIONS[CURRENT_VERSION]
  const old = ENRICHMENT_VERSIONS[oldVersion]
  
  if (!old) {
    return { 
      isNewer: true, 
      newCriteria: current.criteria,
      changelog: []
    }
  }
  
  const newCriteria = current.criteria.filter(c => !old.criteria.includes(c))
  
  return {
    isNewer: newCriteria.length > 0,
    newCriteria,
    changelog: getChangelog(oldVersion, CURRENT_VERSION)
  }
}

/**
 * Genera il changelog tra due versioni
 */
function getChangelog(fromVersion, toVersion) {
  const changes = []
  const versions = Object.keys(ENRICHMENT_VERSIONS).sort()
  
  const fromIndex = versions.indexOf(fromVersion)
  const toIndex = versions.indexOf(toVersion)
  
  if (fromIndex === -1 || toIndex === -1) return []
  
  for (let i = fromIndex + 1; i <= toIndex; i++) {
    const version = versions[i]
    const versionInfo = ENRICHMENT_VERSIONS[version]
    changes.push({
      version,
      date: versionInfo.released,
      description: versionInfo.description,
      newCriteria: versionInfo.new_criteria || []
    })
  }
  
  return changes
}

/**
 * Ottiene le definizioni dei criteri
 */
export function getCriteriaDefinitions() {
  return CRITERIA_DEFINITIONS
}

/**
 * Arricchisce un plot con dati open data
 * @param {Object|string} geojson - Geometria del plot
 * @param {boolean} partialUpdate - Se true, interroga solo i nuovi criteri
 * @param {Object} existingData - Dati esistenti per merge in caso di partial update
 */
export async function enrichPlotWithOpenData(geojson, partialUpdate = false, existingData = null) {
  const geometry = typeof geojson === 'string' ? JSON.parse(geojson) : geojson
  
  if (!geometry) {
    throw new Error('Geometria non valida')
  }
  
  // Determina quali criteri interrogare
  let criteriaToFetch = ENRICHMENT_VERSIONS[CURRENT_VERSION].criteria
  
  if (partialUpdate && existingData?.criteria_used) {
    // Aggiornamento parziale: solo i nuovi criteri
    criteriaToFetch = criteriaToFetch.filter(c => !existingData.criteria_used.includes(c))
    
    if (criteriaToFetch.length === 0) {
      throw new Error('Nessun nuovo criterio da interrogare')
    }
  }
  
  const criteriaDefs = CRITERIA_DEFINITIONS.filter(c => criteriaToFetch.includes(c.guid))
  
  const response = await fetch(GEOPROCESSING_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY
    },
    body: JSON.stringify({
      geometry: geometry.type === 'Feature' ? geometry.geometry : geometry,
      geoprocessing: {
        enabled: true,
        criteria: criteriaDefs.map(c => ({
          id_layer: c.id_layer,
          id_criteria_type: c.id_criteria_type,
          name: c.name,
          guid: c.guid
        }))
      }
    })
  })
  
  if (!response.ok) {
    throw new Error(`Errore HTTP: ${response.status}`)
  }
  
  const data = await response.json()
  
  if (!data.ok) {
    throw new Error(data.message || 'Errore nel geoprocessing')
  }
  
  const formatted = formatResults(data.results)
  
  // Merge con dati esistenti se aggiornamento parziale
  let finalData = formatted
  if (partialUpdate && existingData?.data) {
    finalData = {
      ...formatted,
      ...Object.keys(formatted).reduce((acc, key) => {
        acc[key] = { ...existingData.data[key], ...formatted[key] }
        return acc
      }, {})
    }
  }
  
  return {
    version: CURRENT_VERSION,
    enriched_at: new Date().toISOString(),
    criteria_used: ENRICHMENT_VERSIONS[CURRENT_VERSION].criteria,
    data: finalData,
    partial_update: partialUpdate
  }
}

/**
 * Formatta i risultati del geoprocessing
 */
function formatResults(results) {
  const formatted = {}
  
  for (const crit of results) {
    const def = CRITERIA_DEFINITIONS.find(c => c.guid === crit.id_criteria)
    
    if (!def) continue
    
    // Formatta in base al tipo di criterio
    if (crit.id_criteria === '964cbf8d-97e9-4073-9ebd-88075f8481fd') {
      // Pedologia
      const rawData = crit.result?.data?.[0]
      if (rawData) {
        formatted.pedology = {
          sand: rawData.sab || null,
          silt: rawData.lim || null,
          clay: rawData.arg || null,
          organicMatter: rawData.sostorg || null,
          landUse: rawData.sst_descr || null,
          raw: rawData
        }
      }
    } else if (crit.id_criteria === 'b1c1e8ed-76fa-4f53-850d-8248dc3c1c39') {
      // Temperatura
      if (crit.result?.data?.mean !== undefined) {
        formatted.temperature = {
          meanTemp: crit.result.data.mean,
          raw: crit.result.data
        }
      }
    } else if (crit.id_criteria === '57ae051e-3e6f-4ca2-8816-0c297aa3be2e') {
      // Quota
      if (crit.result?.data?.mean !== undefined) {
        const data = crit.result.data
        formatted.elevation = {
          meanElevation: data.mean,
          minElevation: data.min,
          maxElevation: data.max,
          elevationRange: data.max - data.min,
          raw: data
        }
      }
    }
  }
  
  return formatted
}

/**
 * Decodifica i dati pedologici (legacy compatibility)
 */
function decodePedologyData(record) {
  // Implementazione basata su decode_record del legacy
  // Da implementare se necessario
  return record
}
